import { Button } from 'primereact/button'
import ULModal from '../../../../shared/ul-modal/ULModal'
import PmrE2EProjectDetailsOrderlineModel from '../../shared/interfaces/pmr-e2e-project-details-orderline-model'
import { Field, FieldProps, Form, FormikProvider, useFormik } from 'formik'
import {
  InputNumberAutoFillSync,
  InvoicePriceTemplateInput,
  invoicePriceValidationSchema,
  ulPayment,
} from '../invoice-price-template/InvoicePriceTemplate'
import PmrMultiSelectCurrencies from '../../shared/components/pmr-multiselect-currencies/PmrMultiSelectCurrencies'
import classes from './InvoicePriceModal.module.scss'
import { useSelector, useDispatch } from 'react-redux'
import {
  selectCurrencies,
  updateRefreshInvoiceWarnings,
} from '../../../../features/projectManagementPmr/projectManagementPmrSlice'
import { useEffect, useState } from 'react'
import PmrE2EProjectModel from '../../shared/interfaces/pmr-e2e-project-model'
import UpdateProjectlineCurrencyModel from '../../../../shared/models/currency-models/UpdateProjectlineCurrencyModel'
import { updateOrderLineInvoice } from '../../../../features/projectManagement/projectManagementSlice'
import CurrencyService from '../../../../services/CurrencyService'
import useToastr from '../../../../hooks/useToastr'
import { DataTable } from 'primereact/datatable'
import { Column, ColumnBodyOptions } from 'primereact/column'
import {
  GridColumnModel,
  invoiceAmount,
  invoiceCurrency,
  orderlineInvoiceColumns,
} from '../../data/project-orderline-invoice-columns'
import { Checkbox } from 'primereact/checkbox'
import { get, cloneDeep } from 'lodash'
import ConfirmationModalV2 from '../../../../shared/confirmation-modal-v2/ConfirmationModalV2'
import clsx from 'clsx'
import {
  MAX_INVOICE_PRICE_AMOUNT,
  MAX_INVOICE_PRICE_LENGTH,
} from '../../../../utils/constants/invoice-price-constant'

interface InvoicePriceModalProps {
  visible: boolean
  onHide: () => void
  data: PmrE2EProjectDetailsOrderlineModel[]
  project: PmrE2EProjectModel
}
export default function InvoicePriceModal({
  visible,
  onHide: _onHide,
  project,
  data,
}: InvoicePriceModalProps) {
  const currencies = useSelector(selectCurrencies)
  const dispatch = useDispatch()
  const { showSuccess } = useToastr()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false)
  const [isSaveDisabled, setIsSaveDisabled] = useState(true)

  const [columns, setColumns] = useState<GridColumnModel[]>(orderlineInvoiceColumns)
  const [selectedOrderLine, setSelectedOrderline] = useState<UpdateProjectlineCurrencyModel[]>([])

  const [orderLineModel, setOrderLineModel] = useState<UpdateProjectlineCurrencyModel[]>([])
  const [initialOrderLineModel, setInitialOrderLineModel] = useState<
    UpdateProjectlineCurrencyModel[]
  >([])

  const [selectableOrderline, setSelectableOrderline] = useState<UpdateProjectlineCurrencyModel[]>(
    []
  )

  const invalidInput = selectableOrderline.some(
    line => !line.invoicePriceAmount || line.invoiceCurrencyId === '' || line.invoicePriceAmount < 0
  )

  const invoiceCurrencies = new Set(
    data.filter(item => !!item.invoiceCurrencyCode).map(item => item.invoiceCurrencyCode)
  )
  const defaultCurrencies =
    invoiceCurrencies.size > 0
      ? invoiceCurrencies
      : new Set(data.filter(item => !!item.currency).map(item => item.currency))

  const defaultCurrency =
    defaultCurrencies.size === 1
      ? currencies.find(item => item.code === [...defaultCurrencies][0])
      : null

  const hasEdit = project.e2EProjectLineMilestoneGroupItems.some(
    milestone => milestone.name === ulPayment
  )

  const onHide = () => {
    formik.resetForm()
    _onHide()
  }

  const onChangeValueValidation = () => {
    return JSON.stringify(initialOrderLineModel) === JSON.stringify(orderLineModel)
  }

  const handleCancel = () => {
    if (!onChangeValueValidation()) {
      setIsConfirmationVisible(true)
    } else {
      onHide()
    }
  }

  const onSubmit = async () => {
    setIsSubmitting(true)

    try {
      const request = orderLineModel.filter(
        (line: UpdateProjectlineCurrencyModel) => line.e2eProjectLineId !== ''
      )

      await CurrencyService.updateProjectlineCurrency(request)
      dispatch(updateOrderLineInvoice(request))
      onHide()
      showSuccess(
        'Invoice Price Updated',
        `Invoice Price for selected Order Lines successfully updated.`
      )
      dispatch(updateRefreshInvoiceWarnings(true))
    } catch (e) {
      console.error(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const formik = useFormik<InvoicePriceTemplateInput>({
    initialValues: {
      invoicePriceAmount: null,
      invoicePriceCurrency: defaultCurrency?.id,
    },
    onSubmit,
    validationSchema: invoicePriceValidationSchema,
    enableReinitialize: true,
    isInitialValid: false,
  })

  const onChangeInvoiceAmount = (e: any, rowIndex: number) => {
    let updatedData: UpdateProjectlineCurrencyModel[] = []
    if (rowIndex === 0) {
      updatedData = orderLineModel.map((state: UpdateProjectlineCurrencyModel) => {
        if (
          selectedOrderLine.some(
            (selected: any) => selected.e2eProjectLineId === state.e2eProjectLineId
          ) &&
          state.hasUlPayment &&
          e.value <= MAX_INVOICE_PRICE_AMOUNT
        )
          state.invoicePriceAmount = e.value
        return state
      })
    } else {
      updatedData = orderLineModel.map((state: any, index: number) => {
        if (index === rowIndex && e.value <= MAX_INVOICE_PRICE_AMOUNT)
          state.invoicePriceAmount = e.value
        return state
      })
    }

    setOrderLineModel(updatedData)
    setIsSaveDisabled(onChangeValueValidation())
  }

  const onChangeCurrency = (e: any, data: UpdateProjectlineCurrencyModel, rowIndex: number) => {
    let updatedData: UpdateProjectlineCurrencyModel[] = []
    const selectedCurrency = currencies.find(item => item.id === e.value)

    if (rowIndex === 0) {
      updatedData = orderLineModel.map((state: UpdateProjectlineCurrencyModel, index: number) => {
        if (
          index === 0 ||
          selectedOrderLine.some(
            (selected: any) => selected.e2eProjectLineId === state.e2eProjectLineId
          )
        ) {
          state.invoiceCurrencyId = e.value
          state.invoiceCurrencyCode = selectedCurrency?.code
        }
        return state
      })
    } else {
      updatedData = orderLineModel.map((state: any, index: number) => {
        if (index === rowIndex) {
          state.invoiceCurrencyId = e.value
          state.invoiceCurrencyCode = selectedCurrency?.code
        }
        return state
      })
    }

    setOrderLineModel(updatedData)
    setIsSaveDisabled(onChangeValueValidation())
  }

  //TABLE HEADER
  const checkboxColumnHeader = (col: GridColumnModel) => {
    const nonUlPayment = selectableOrderline?.length === 0
    const selected = selectedOrderLine.filter(
      item => item.e2eProjectNumber !== '' && item.hasUlPayment
    )
    const isAllSelected = selectableOrderline?.length === selected?.length

    return (
      <Checkbox
        checked={isAllSelected && !nonUlPayment}
        onChange={e => {
          if (e.checked) {
            setSelectedOrderline(orderLineModel)
          } else {
            setSelectedOrderline([])
          }
        }}
        disabled={nonUlPayment}
      />
    )
  }

  const tableColumnHeader = (col: GridColumnModel) => (
    <div>
      <span title={col.value}>{col.value}</span>
    </div>
  )

  const dynamicHeaderBody = (col: GridColumnModel) => {
    return tableColumnHeader(col)
  }
  //END TABLE HEADER

  //TABLE BODY

  const invoiceAmountColumnBody = (
    rowData: UpdateProjectlineCurrencyModel,
    col: ColumnBodyOptions
  ) => {
    const rowValue = rowData?.invoicePriceAmount
    const rowIndex = col.rowIndex
    const selectedCount = selectedOrderLine.length
    const isNegativeValue =
      rowValue !== null && rowValue < 0 && (selectedCount === 1 || rowIndex !== 0)

    const isForBulk = orderLineModel.length > 1 && rowIndex === 0
    const isDisabled = isSubmitting || selectableOrderline?.length === 0

    if (!rowData.hasUlPayment && !isForBulk) {
      return <div className={classes['invoice-na-value']}>N/A</div>
    }

    return (
      <InputNumberAutoFillSync
        inputId="invoiceAmount"
        className={clsx({ 'p-invalid': isNegativeValue }, classes['invoice-amount'])}
        disabled={isDisabled}
        value={rowValue}
        onChange={e => onChangeInvoiceAmount(e, rowIndex)}
        maxLength={MAX_INVOICE_PRICE_LENGTH + 1} // +1 for decimal point
      />
    )
  }

  const invoiceCurrencyColumnBody = (
    rowData: UpdateProjectlineCurrencyModel,
    col: ColumnBodyOptions
  ) => {
    const rowValue = rowData.invoiceCurrencyId
    const rowIndex = col.rowIndex
    const isForBulk = orderLineModel.length > 1 && rowIndex === 0
    const isDisabled = isSubmitting || selectableOrderline?.length === 0

    if (!rowData.hasUlPayment && !isForBulk) {
      return <div className={classes['invoice-na-value']}>N/A</div>
    }

    return (
      <PmrMultiSelectCurrencies
        inputId="invoiceCurrency"
        disabled={isDisabled}
        value={rowValue}
        onChange={(e: any) => {
          onChangeCurrency(e, rowData, rowIndex)
        }}
        className={classes['invoice-currency']}
      />
    )
  }

  const checkboxColumnBody = (rowData: UpdateProjectlineCurrencyModel) => {
    if (rowData.e2eProjectLineId === '') return ''
    const checked = selectedOrderLine.some(
      item => item.e2eProjectLineId === rowData.e2eProjectLineId && rowData.hasUlPayment
    )

    return (
      <Checkbox
        checked={checked}
        disabled={!rowData.hasUlPayment}
        onChange={e => {
          let updated = selectedOrderLine.filter(
            item => item.e2eProjectLineId !== rowData.e2eProjectLineId
          )
          if (e.checked) {
            updated = [...selectedOrderLine, rowData]
          }

          setSelectedOrderline(updated)
        }}
      />
    )
  }

  const tableColumnBody = (rowData: UpdateProjectlineCurrencyModel, col: ColumnBodyOptions) => {
    return (
      <div className={classes[`invoice-${col.field}`]} title={get(rowData, col.field)}>
        {get(rowData, col.field)}
      </div>
    )
  }

  const dynamicColumnBody = (col: GridColumnModel) => {
    if (col.id === invoiceAmount) {
      return invoiceAmountColumnBody.bind(col)
    } else if (col.id === invoiceCurrency) {
      return invoiceCurrencyColumnBody.bind(col)
    }

    return tableColumnBody.bind(col)
  }

  //END TABLE BODY

  const bodyTemplate = (
    <div>
      <div>You have unsaved changes to the invoice pricing.</div>
      <div>Any unsaved changes will be lost.</div>
      <br />
      <div>Would you like to continue?</div>
    </div>
  )

  const dynamicColumns = columns.map((col: GridColumnModel) => {
    const columnStyle: React.CSSProperties = {
      width: col.width,
    }

    return (
      <Column
        field={col.id}
        header={dynamicHeaderBody(col)}
        alignFrozen="left"
        body={dynamicColumnBody(col)}
        style={columnStyle}
      />
    )
  })

  useEffect(() => {
    let updateData: UpdateProjectlineCurrencyModel[] = []
    const ulPayment = 'UL: Payment'

    if (data?.length > 1) {
      const initialModel: UpdateProjectlineCurrencyModel = {
        e2eProjectNumber: '',
        e2eProjectLineId: '',
        e2eOrderlineNumber: '',
        invoicePriceAmount: null,
        invoiceCurrencyCode: '',
        invoiceCurrencyId: '',
      }

      updateData.push(initialModel)
    }

    data.forEach(item => {
      var hasULPayment = project.e2EProjectLineMilestoneGroupItems.some(
        x => x.e2EProjectLineId == item.id && x.name === ulPayment
      )

      const newModel: UpdateProjectlineCurrencyModel = {
        e2eProjectNumber: item.projectNumber ?? '',
        e2eProjectLineId: item.id,
        e2eOrderlineNumber: item.orderLineNumber ?? '',
        invoicePriceAmount: item.invoicePriceAmount ?? 0,
        invoiceCurrencyCode: item.invoiceCurrencyCode ?? '',
        invoiceCurrencyId: item.invoiceCurrencyId ?? '',
        hasUlPayment: hasULPayment,
      }

      updateData.push(newModel)
    })

    const selectable = updateData.filter(line => line.e2eProjectNumber !== '' && line.hasUlPayment)

    setSelectableOrderline(selectable)
    setSelectedOrderline(selectable)
    setOrderLineModel(updateData)
    setInitialOrderLineModel(cloneDeep(updateData))
  }, [])

  return (
    <>
      <ULModal
        visible={visible && hasEdit}
        onHide={handleCancel}
        header="Input Invoice Price"
        style={{ width: '70vw', height: '70vh' }}
        footer={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button className="default-cancel-button" onClick={handleCancel} label="CANCEL" />
            <Button
              className="default-save-button"
              form="invoice-price-form-modal"
              type="submit"
              disabled={isSaveDisabled || invalidInput}
              loading={isSubmitting}
              label="SAVE"
              onClick={onSubmit}
            />
          </div>
        }
      >
        <div style={{ marginBottom: '15px' }}>
          <div>Enter Invoice Amount and Currency for selected Order Lines.</div>
          <div>
            <i>
              <b>Note:</b> If selected Order Line's Invoice Pricing is set to 'N/A', it cannot be
              edited or updated.
            </i>
          </div>
        </div>

        <DataTable dataKey="id" size="small" value={orderLineModel} selectionMode="checkbox">
          <Column
            style={{ width: '2px' }}
            header={checkboxColumnHeader}
            body={checkboxColumnBody}
          />
          {dynamicColumns}
        </DataTable>
      </ULModal>

      <ConfirmationModalV2
        headerDisplay="Unsaved Invoice Pricing Confirmation"
        buttonNoDisplay="NO"
        buttonYesDisplay="YES"
        className={classes['dialog']}
        confirmationMessageHTML={bodyTemplate}
        onNegative={() => setIsConfirmationVisible(false)}
        onPositive={() => {
          setIsConfirmationVisible(false)
          onHide()
        }}
        onClose={() => setIsConfirmationVisible(false)}
        visible={isConfirmationVisible}
        style={{ width: '30vw' }}
      />
    </>
  )
}
